import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Network, Plus, Search, Square } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { apiJson } from "@/api/apiClient";
import AddSourcesModal from "@/features/sources/components/AddSourcesModal";
import SourceJobStatus from "@/features/sources/components/SourceJobStatus";
import SourceList from "@/features/sources/components/SourceList";
import useGraphBuildJobPolling from "@/features/sources/hooks/useGraphBuildJobPolling";
import { useSourcesMutations } from "@/features/sources/hooks/useSourcesMutations";
import useSourcesSelection from "@/features/sources/hooks/useSourcesSelection";

interface Document {
	id: string;
	title: string;
	contentPath?: string;
	metadata?: Record<string, unknown>;
	createdAt?: string;
	updatedAt?: string;
}

interface SourcesPanelProps {
	selectedSources: Set<string>;
	onSourceToggle: (sourceId: string) => void;
	onSelectAll: (selected: boolean, documentIds: string[]) => void;
	onGraphGenerated?: (graphId: string) => void;
	workspaceId?: string | null;
	/** When true, panel is embedded in left sidebar tabs (no header/collapse, parent provides chrome). */
	embeddedInTabs?: boolean;
}

const fetchDocuments = async (ctx: QueryFunctionContext): Promise<Document[]> => {
	const result = await apiJson<{ documents: Document[] }>("/documents", {
		signal: ctx.signal,
	});
	return result.documents;
};

const SourcesPanel: React.FC<SourcesPanelProps> = ({
	selectedSources,
	onSourceToggle,
	onSelectAll,
	onGraphGenerated,
	workspaceId,
	embeddedInTabs = false,
}) => {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [openMenuId, setOpenMenuId] = useState<string | null>(null);
	const menuRefs = useRef<Map<string, HTMLDivElement>>(new Map());

	const { data: documents = [], isLoading } = useQuery({
		queryKey: ["documents"],
		queryFn: fetchDocuments,
	});

	const { searchQuery, setSearchQuery, filteredDocuments, allSelected } = useSourcesSelection({
		documents,
		selectedSources,
	});

	const { currentJobId, setCurrentJobId, jobStatus, clearJob } = useGraphBuildJobPolling({
		onGraphGenerated,
	});

	const { deleteMutation, generateGraphMutation, quotaMessage, setQuotaMessage } = useSourcesMutations({
		workspaceId,
		selectedSources,
		openMenuId,
		onSourceToggle,
		onJobStarted: setCurrentJobId,
	});

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!openMenuId) return;
			const menuElement = menuRefs.current.get(openMenuId);
			if (menuElement && !menuElement.contains(event.target as Node)) {
				setOpenMenuId(null);
			}
		};

		if (!openMenuId) return;

		const timeoutId = setTimeout(() => {
			document.addEventListener("mousedown", handleClickOutside);
		}, 0);

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [openMenuId]);

	const handleDelete = (documentId: string, event: React.MouseEvent) => {
		event.stopPropagation();
		event.preventDefault();
		if (window.confirm("Are you sure you want to delete this document?")) {
			deleteMutation.mutate(documentId);
			setOpenMenuId(null);
		}
	};

	if (!embeddedInTabs && isCollapsed) {
		return (
			<div className="w-12 surface-alt border-r border-strong flex flex-col items-center py-4">
				<button
					type="button"
					onClick={() => setIsCollapsed(false)}
					className="p-2 soft-hover rounded-lg transition-colors"
					aria-label="Expand sources panel"
				>
					<Square className="w-5 h-5 text-muted" />
				</button>
			</div>
		);
	}

	const content = (
		<>
			{!embeddedInTabs && (
				<div className="p-4 border-b border-subtle surface-alt flex items-center justify-between">
					<h2 className="text-base font-semibold text-primary">Sources</h2>
					<button
						type="button"
						onClick={() => setIsCollapsed(true)}
						className="p-1 soft-hover rounded-lg transition-colors"
						aria-label="Collapse sources panel"
					>
						<Square className="w-4 h-4 text-muted" />
					</button>
				</div>
			)}

			<div className="p-4 border-b border-subtle surface-alt">
				<button
					type="button"
					onClick={() => setIsModalOpen(true)}
					className="w-full flex items-center justify-center gap-2 px-4 py-2.5 btn btn-secondary font-medium text-sm"
				>
					<Plus className="w-4 h-4" />
					<span>Add sources</span>
				</button>
			</div>

			<AddSourcesModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

			<div className="p-4 border-b border-subtle surface-alt">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-subtle" />
					<input
						type="text"
						placeholder="Search the web for new"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full pl-10 pr-3 py-2 border border-strong surface-muted text-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					/>
				</div>
			</div>

			{documents.length > 0 && (
				<div className="px-4 py-3 border-b border-subtle surface-alt">
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={allSelected}
							onChange={(e) =>
								onSelectAll(
									e.target.checked,
									documents.map((document) => document.id),
								)
							}
							className="w-4 h-4 text-primary-600 border-strong rounded focus:ring-primary-500"
						/>
						<span className="text-sm font-medium text-muted">Select all sources</span>
					</label>
				</div>
			)}

			{currentJobId && (
				<div className="p-4 border-b border-subtle surface-alt">
					<SourceJobStatus currentJobId={currentJobId} jobStatus={jobStatus} onClearJob={clearJob} />
				</div>
			)}

			{selectedSources.size > 0 && (
				<div className="p-4 border-b border-subtle surface-alt space-y-3">
					{quotaMessage && (
						<div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
							<div className="flex items-start gap-2">
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								<p>{quotaMessage}</p>
							</div>
						</div>
					)}
					<button
						type="button"
						onClick={() => {
							setQuotaMessage(null);
							generateGraphMutation.mutate(Array.from(selectedSources));
						}}
						disabled={generateGraphMutation.isPending || !!currentJobId}
						className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{generateGraphMutation.isPending ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								<span>Starting...</span>
							</>
						) : (
							<>
								<Network className="w-4 h-4" />
								<span>Generate graph ({selectedSources.size})</span>
							</>
						)}
					</button>
				</div>
			)}

			<div className="flex-1 surface-alt overflow-y-auto min-h-0">
				{isLoading ? (
					<div className="p-4 text-center text-sm text-subtle">Loading sources...</div>
				) : filteredDocuments.length === 0 ? (
					<div className="p-4 text-center text-sm text-subtle">
						{searchQuery ? "No sources found" : "No sources yet"}
					</div>
				) : (
					<SourceList
						documents={filteredDocuments}
						selectedSources={selectedSources}
						openMenuId={openMenuId}
						menuRefs={menuRefs}
						onSourceToggle={onSourceToggle}
						onToggleMenu={(documentId) => setOpenMenuId((prev) => (prev === documentId ? null : documentId))}
						onDelete={handleDelete}
						deletePending={deleteMutation.isPending}
					/>
				)}
			</div>
		</>
	);

	const wrapperClass = embeddedInTabs
		? "flex flex-col h-full min-h-0 w-full"
		: "w-80 surface-alt border-r border-strong flex flex-col h-full";

	return <div className={wrapperClass}>{content}</div>;
};

export default SourcesPanel;
